import React, { useState, useEffect } from "react";
import { AiFillCloseCircle } from "react-icons/ai";
import { useStores } from "../../store";

const ProxyReasonDialog = ({ isOpen, onClose, onSelectReason, candidate }) => {
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [showOtherReason, setShowOtherReason] = useState(false);
  const [otherReasonValue, setOtherReasonValue] = useState("");
  const [customReasons, setCustomReasons] = useState([]);
  const { RecuiterStore } = useStores();

  // Predefined proxy reasons
  const proxyReasons = [
    "Phone detected during interview",
    "Unauthorized reference materials detected",
    "Third-party assistance detected",
    "Multiple devices or screen sharing detected",
    "Suspicious background activity detected",
    "Noticed reading from external sources while answering",
  ];

  // Load custom reasons from localStorage when modal opens
  useEffect(() => {
    if (isOpen) {
      try {
        const savedCustomReasons = localStorage.getItem("proxyCustomReasons");
        if (savedCustomReasons) {
          const parsed = JSON.parse(savedCustomReasons);
          setCustomReasons(Array.isArray(parsed) ? parsed : []);
        }
      } catch (error) {
        console.error("Error loading custom reasons from localStorage:", error);
        setCustomReasons([]);
      }
    }
  }, [isOpen]);

  // Save custom reasons to localStorage
  const saveCustomReasonsToStorage = (reasons) => {
    try {
      localStorage.setItem("proxyCustomReasons", JSON.stringify(reasons));
    } catch (error) {
      console.error("Error saving custom reasons to localStorage:", error);
    }
  };

  const handleToggleReason = (reason) => {
    setSelectedReasons((prev) => {
      const isCustomReason = !proxyReasons.includes(reason);
      
      if (prev.includes(reason)) {
        // Removing reason
        const newSelected = prev.filter((r) => r !== reason);
        
        // If it's a custom reason, remove it from localStorage
        if (isCustomReason) {
          const updatedCustomReasons = customReasons.filter((r) => r !== reason);
          setCustomReasons(updatedCustomReasons);
          saveCustomReasonsToStorage(updatedCustomReasons);
        }
        
        return newSelected;
      } else {
        // Adding reason
        return [...prev, reason];
      }
    });
  };

  const handleAddOtherReason = () => {
    const trimmedValue = otherReasonValue.trim();
    if (trimmedValue && !selectedReasons.includes(trimmedValue)) {
      setSelectedReasons((prev) => [...prev, trimmedValue]);
      
      // Add to custom reasons and save to localStorage
      if (!customReasons.includes(trimmedValue)) {
        const updatedCustomReasons = [...customReasons, trimmedValue];
        setCustomReasons(updatedCustomReasons);
        saveCustomReasonsToStorage(updatedCustomReasons);
      }
      
      setOtherReasonValue("");
      setShowOtherReason(false);
    }
  };

  const handleConfirm = () => {
    if (selectedReasons.length === 0) {
      alert("Please select at least one reason.");
      return;
    }
    onSelectReason(selectedReasons);
    setSelectedReasons([]);
    setOtherReasonValue("");
    setShowOtherReason(false);
    onClose();
  };

  const handleCancel = () => {
    setSelectedReasons([]);
    setOtherReasonValue("");
    setShowOtherReason(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg w-[90%] max-w-2xl mx-4 md:mx-auto relative shadow-lg flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold text-gray-800">
            Select Proxy Reason(s)
          </h2>
          <AiFillCloseCircle
            className="text-primary text-3xl cursor-pointer hover:opacity-75"
            onClick={handleCancel}
          />
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-sm text-gray-600 mb-4">
            Select one or more reasons (click to toggle):
          </p>
          <div className="flex flex-wrap gap-3">
            {proxyReasons.map((reason) => {
              const isSelected = selectedReasons.includes(reason);
              return (
                <button
                  key={reason}
                  onClick={() => handleToggleReason(reason)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    isSelected
                      ? "bg-primary text-white shadow-md"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {reason}
                </button>
              );
            })}
            {/* Display custom reasons from localStorage */}
            {customReasons.map((reason) => {
              const isSelected = selectedReasons.includes(reason);
              return (
                <button
                  key={reason}
                  onClick={() => handleToggleReason(reason)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    isSelected
                      ? "bg-primary text-white shadow-md"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {reason}
                </button>
              );
            })}
            <button
              onClick={() => setShowOtherReason(!showOtherReason)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                showOtherReason
                  ? "bg-primary text-white shadow-md"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Other
            </button>
            {showOtherReason && (
              <input
                type="text"
                value={otherReasonValue}
                onChange={(e) => setOtherReasonValue(e.target.value)}
                placeholder="Enter other reason"
                className="px-4 py-2 rounded-full text-sm font-medium transition-all bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-primary focus:outline-none focus:ring-2 focus:ring-primary"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddOtherReason();
                  }
                }}
                autoFocus
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 transition-colors"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};



export default ProxyReasonDialog;